import React from 'react';
import { cn } from '@/utils/cn';

export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Required: an icon alone says nothing to a screen reader. Also used as the tooltip. */
  label: string;
  /** Selected / toggled-on state: the brand shows a selected icon in orange on a soft chip. */
  active?: boolean;
  /** `xs` = 24px (dense card rows, 36px on touch), `sm` = 32px chip, `md` = 40px chip. sm/md grow to 44px on coarse pointers. */
  size?: 'xs' | 'sm' | 'md';
  /** `accent` tints hover and active violet, for AI / voice controls. */
  tone?: 'default' | 'accent';
  children: React.ReactNode;
}

/**
 * Icon-only control. Steel Blue icon on a transparent chip; hover fills the chip and, per the brand, outlines it in
 * the orange -> violet gradient; selected turns the icon orange. Meets the 44x44 target on touch devices.
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, active = false, size = 'md', tone = 'default', className, children, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center rounded-full border border-transparent touch-manipulation',
        'transition-[background-color,color,border-color,box-shadow] duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui',
        'disabled:pointer-events-none disabled:opacity-50',
        size === 'xs'
          ? 'h-6 w-6 [@media(pointer:coarse)]:h-9 [@media(pointer:coarse)]:w-9'
          : cn(size === 'sm' ? 'h-8 w-8' : 'h-10 w-10', '[@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11'),
        active
          ? tone === 'accent'
            ? 'bg-accent-soft text-accent-text'
            : 'bg-primary-soft text-primary-text'
          : 'text-icon hover:bg-ui-hover btn-icon-hover',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  ),
);

IconButton.displayName = 'IconButton';

export { IconButton };
